import { useMemo } from "react";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRevealWage, useViewWageAllowed } from "../../hooks/useRevealWage";
import { isHeadNurse } from "../../utils/utilFunctions";

export type BreakdownLinkAccess = {
	/** Team → Wage Settings (rule lines, "Rate rules for this department"). */
	canOpenWageSettings: boolean;
	/** The nurse's profile ("Edit <name>'s wage"). */
	canOpenProfile: boolean;
	/** Team → Agency Configuration (an agency shift's rates). */
	canOpenAgencyConfiguration: boolean;
};

/**
 * DAT-906 · Which of the wage breakdown's links the viewer could actually follow. A link to somewhere they
 * cannot go is dropped, never rendered broken — each answer mirrors the gate at the destination:
 *
 * - Team and the profile route are head-nurse routes (`App.tsx`).
 * - Wage Settings follows the wage PERMISSION (`useViewWageAllowed`), not the reveal toggle: its links show
 *   no figure, so hiding amounts from someone at the screen is no reason to drop them. Same for the
 *   profile, where the wage is edited.
 * - The Agency Configuration tab shows only with wages revealed and outside medical departments
 *   (`showAgencyTab` in `Team.tsx`) — the same condition here, so the link never lands on the Team tab.
 */
export const useBreakdownLinkAccess = (): BreakdownLinkAccess => {
	const { userInfo } = useAuth();
	const { isMedicalDepartment } = useHospitalData();
	const viewWageAllowed = useViewWageAllowed();
	const revealWage = useRevealWage();
	const headNurse = isHeadNurse(userInfo.role);
	// Memoised: the open breakdown shares this through context, so a fresh object would re-render every card.
	return useMemo(
		() => ({
			canOpenWageSettings: headNurse && viewWageAllowed,
			canOpenProfile: headNurse && viewWageAllowed,
			canOpenAgencyConfiguration: headNurse && revealWage && !isMedicalDepartment,
		}),
		[headNurse, viewWageAllowed, revealWage, isMedicalDepartment],
	);
};
